import type {
  RuteroOfferOptionsSelection,
  RuteroTicketQuantity,
  RuteroTourDetail,
  RuteroTourSession,
} from '@bandido/shared/types';
import { RUTERO_SELECTION_TICKET_QUANTITIES_MAX } from '@bandido/shared/types';

import type { OfferOptions, OfferOptionsRequest } from './queries';

/** Ausente permite discovery; presente siempre respeta el mínimo contractual. */
export function tourOptionsPartySize(count: number): number | undefined {
  return Number.isInteger(count) && count > 0 ? count : undefined;
}

/** Discovery puro: provenance + composición conocida + selección, nunca PII. */
export function tourOptionsDiscoveryRequest(
  provenanceToken: string,
  partySize: number | undefined,
  selection: RuteroOfferOptionsSelection = {},
): OfferOptionsRequest {
  const knownPartySize = tourOptionsPartySize(partySize ?? 0);
  return {
    provenanceToken,
    ...(knownPartySize ? { partySize: knownPartySize } : {}),
    ...(Object.keys(selection).length ? { selection } : {}),
  };
}

/** Una respuesta vieja nunca puede reemplazar options de una selección nueva. */
export function tourOptionsResponseIsCurrent(
  completedRequest: number,
  latestRequest: number,
): boolean {
  return completedRequest === latestRequest;
}

/** Conserva únicamente la misma identidad opaca; jamás elige una sustituta. */
export function reconcileTourSession(selectedId: string, sessions: readonly RuteroTourSession[] | undefined): string {
  if (!selectedId || !sessions) return selectedId;
  return sessions.some((session) => session.id === selectedId) ? selectedId : '';
}

/** Un error tardío solo puede invalidar la misma sesión que lo originó. */
export function shouldClearTourSessionAfterPreviewError(
  errorCode: string | undefined,
  requestedSessionId: string | undefined,
  liveSessionId: string,
): boolean {
  return (
    errorCode === 'TOUR_SELECTION_UNAVAILABLE' &&
    Boolean(liveSessionId) &&
    requestedSessionId === liveSessionId
  );
}

export function tourSessionLabel(session: RuteroTourSession): string {
  return [session.date, session.time, session.language, session.modality?.name, session.fareBand?.name]
    .filter(Boolean)
    .join(' · ');
}

/** Valida la edad ya resuelta para el día de servicio antes de cotizar Tours. */
export function tourQuoteAge(value: string | undefined): number | null {
  const normalized = value?.trim() ?? '';
  if (!/^\d{1,3}$/.test(normalized)) return null;
  const age = Number(normalized);
  return age >= 0 && age <= 120 ? age : null;
}

/** Una edad por participante, en el mismo orden de la composición elegida. */
export function tourQuoteAges(
  drafts: readonly string[],
  partySize: number,
): number[] | null {
  if (!Number.isInteger(partySize) || partySize < 1 || drafts.length !== partySize) {
    return null;
  }
  const ages = drafts.map(tourQuoteAge);
  return ages.every((age): age is number => age !== null) ? ages : null;
}

/** Si la composición ya traía edades reales se reutilizan; lo desconocido queda vacío. */
export function tourQuoteAgeDrafts(party: readonly { age?: number }[]): string[] {
  return party.map(({ age }) =>
    typeof age === 'number' && Number.isInteger(age) && age >= 0 && age <= 120
      ? String(age)
      : '');
}

/**
 * Requisito no personal de la sesión elegida. Una vez que Rutero acepta las
 * edades puede dejar de publicarlas como faltantes; por eso la identidad de la
 * sesión que abrió el formulario mantiene ese mismo draft hasta el quote.
 */
export function tourQuoteAgeContext(
  ageDrafts: readonly string[],
  partySize: number,
  sessions: readonly RuteroTourSession[] | undefined,
  selectedId: string,
  capturedSessionId = '',
): { required: boolean; ages: number[] | null } {
  const session = sessions?.find((candidate) => candidate.id === selectedId);
  const required =
    session?.quoteRequirements?.includes('ages') === true ||
    (Boolean(selectedId) && capturedSessionId === selectedId);
  return {
    required,
    // `quoteRequirements` enumera lo que TODAVÍA falta. Cuando Rutero acepta
    // las edades puede retirar `ages`, pero el draft capturado para ESA sesión
    // debe seguir viajando al quote autoritativo.
    ages: required ? tourQuoteAges(ageDrafts, partySize) : null,
  };
}

export function tourTicketQuantity(
  selection: readonly RuteroTicketQuantity[],
  id: string,
): number {
  return selection.find((entry) => entry.id === id)?.quantity ?? 0;
}

/** Con pickups publicados, solo un id vigente habilita descubrir las sesiones. */
export function tourPickupSelectionValid(
  pickups: readonly { id: string }[] | undefined,
  pickupId: string,
): boolean {
  return !pickups?.length || pickups.some((pickup) => pickup.id === pickupId);
}

/** Mantiene el último options completo mientras la mutación entra en pending. */
export function visibleTourOptions(
  current: OfferOptions | undefined,
  settled: OfferOptions | undefined,
): OfferOptions | undefined {
  return current ?? settled;
}

/** Al cambiar pickup se conservan sus alternativas, pero no sesiones del anterior. */
export function tourOptionsAwaitingPickup(
  options: OfferOptions | undefined,
): OfferOptions | undefined {
  if (!options) return undefined;
  const { sessions: _sessions, ...withoutSessions } = options;
  return withoutSessions;
}

/** El click del asesor es la única fuente de cantidades; nunca salen del tipo de pax. */
export function changeTourTicketQuantity(
  selection: readonly RuteroTicketQuantity[],
  id: string,
  delta: 1 | -1,
): RuteroTicketQuantity[] {
  const quantity = tourTicketQuantity(selection, id) + delta;
  return quantity <= 0
    ? selection.filter((entry) => entry.id !== id)
    : [
      ...selection.filter((entry) => entry.id !== id),
      { id, quantity },
    ];
}

export function tourTicketQuantitiesValid(
  session: RuteroTourSession | undefined,
  selection: readonly RuteroTicketQuantity[],
): boolean {
  if (!session) return selection.length === 0;
  const required =
    session.quoteRequirements?.includes('ticket_quantities') === true;
  if (!required && selection.length === 0) return true;
  const options = session.ticketOptions ?? [];
  if (
    !options.length ||
    !selection.length ||
    selection.length > RUTERO_SELECTION_TICKET_QUANTITIES_MAX
  ) {
    return false;
  }
  const ids = new Set<string>();
  let total = 0;
  for (const entry of selection) {
    const option = options.find((candidate) => candidate.id === entry.id);
    if (
      !option ||
      ids.has(entry.id) ||
      !Number.isInteger(entry.quantity) ||
      entry.quantity < 1 ||
      entry.quantity > 20 ||
      (option.minQuantity !== undefined && entry.quantity < option.minQuantity) ||
      (option.maxQuantity !== undefined && entry.quantity > option.maxQuantity) ||
      (option.availability !== undefined && entry.quantity > option.availability)
    ) {
      return false;
    }
    ids.add(entry.id);
    total += entry.quantity;
  }
  return (
    (session.minQuantity === undefined || total >= session.minQuantity) &&
    (session.maxQuantity === undefined || total <= session.maxQuantity) &&
    (session.availability === undefined || total <= session.availability)
  );
}

/**
 * `quoteRequirements` enumera lo que todavía falta. Una vez aceptadas las
 * cantidades Rutero puede retirarlas de esa lista, pero las opciones
 * publicadas y la selección vigente deben seguir editables.
 */
export function tourTicketQuantitiesVisible(
  session: RuteroTourSession,
  selection: readonly RuteroTicketQuantity[],
): boolean {
  return (
    session.quoteRequirements?.includes('ticket_quantities') === true ||
    Boolean(session.ticketOptions?.length) ||
    selection.length > 0
  );
}

export function canAddTourTicketQuantity(
  session: RuteroTourSession,
  selection: readonly RuteroTicketQuantity[],
  id: string,
): boolean {
  const option = session.ticketOptions?.find((candidate) => candidate.id === id);
  if (!option) return false;
  const current = tourTicketQuantity(selection, id);
  const total = selection.reduce((sum, entry) => sum + entry.quantity, 0);
  return (
    (current > 0 || selection.length < RUTERO_SELECTION_TICKET_QUANTITIES_MAX) &&
    current < 20 &&
    (option.maxQuantity === undefined || current < option.maxQuantity) &&
    (option.availability === undefined || current < option.availability) &&
    (session.maxQuantity === undefined || total < session.maxQuantity) &&
    (session.availability === undefined || total < session.availability)
  );
}

export interface TourDetailLine {
  kind: 'description' | 'included' | 'excluded' | 'meeting' | 'instruction' | 'remark';
  text: string;
}

/** Proyecta solo texto comercial que Rutero publicó; no interpreta ni completa contenido. */
export function tourDetailLines(detail: RuteroTourDetail): TourDetailLine[] {
  return [
    ...(detail.description ? [{ kind: 'description' as const, text: detail.description }] : []),
    ...(detail.inclusions ?? []).map((text) => ({
      kind: 'included' as const,
      text,
    })),
    ...(detail.exclusions ?? []).map((text) => ({
      kind: 'excluded' as const,
      text,
    })),
    ...(detail.meetingPoint?.description ? [{ kind: 'meeting' as const, text: detail.meetingPoint.description }] : []),
    ...(detail.meetingPoint?.instructions ?? []).map((text) => ({
      kind: 'instruction' as const,
      text,
    })),
    ...(detail.remarks ?? []).map(({ text }) => ({
      kind: 'remark' as const,
      text,
    })),
  ];
}
